import React, { useEffect, useMemo, useState } from 'react'
import { useShallow } from 'zustand/react/shallow'

import {
  useSelectedVisibleIndex,
  useVisibleFilters,
} from '../hooks/useVisibleFilters'
import { useFiltersStore } from '../state/filtersStore'
import { useNavigationStore } from '../state/navigationStore'
import { FilterQuestion } from './FilterQuestion'

const Wizard = ({ inModal }: { inModal?: boolean }) => {
  const { setWizardOpen, navigateScreen, navigateTab } = useNavigationStore(
    useShallow((state) => ({
      setWizardOpen: state.setWizardOpen,
      navigateScreen: state.navigateScreen,
      navigateTab: state.navigateTab,
    }))
  )

  const {
    selectedFilter,
    guideCompleted,
    setGuideUsed,
    selectNextGuideFilter,
    selectedFilterSection,
    appliedFilters,
    addOrUpdateAppliedFilter,
    selectPreviousGuideFilter,
    isFirstVisible,
  } = useFiltersStore(
    useShallow((state) => ({
      selectedFilter: state.selectedFilter,
      guideCompleted: state.guideCompleted,
      setGuideUsed: state.setGuideUsed,
      selectNextGuideFilter: state.selectNextGuideFilter,
      selectedFilterSection: state.getSelectedFilterSection(),
      appliedFilters: state.appliedFilters,
      addOrUpdateAppliedFilter: state.addOrUpdateAppliedFilter,
      selectPreviousGuideFilter: state.selectPreviousGuideFilter,
      isFirstVisible: state.isSelectedFilterFirstVisible(),
    }))
  )

  const [value, setValue] = useState<boolean | string | undefined>(undefined)
  const [descExpanded, setDescExpanded] = useState(false)

  useEffect(() => {
    // Find already applied value for this filter (if any)
    const foundAlreadyAppliedFilter = selectedFilter
      ? appliedFilters.find((f) => f.acf_name === selectedFilter.acf_name)
      : undefined

    setValue(foundAlreadyAppliedFilter?.value ?? undefined)
    setDescExpanded(false)
  }, [selectedFilter, appliedFilters])

  return (
    <div className='wizard'>
      {/* When shown on start screen, hacky way to show modal header with hidden close button */}
      {!inModal && (
        <header className='modal__header'>
          <button
            className={[
              'modal__header-back-btn',
              isFirstVisible && 'modal__header-back-btn--hidden',
            ].join(' ')}
            onClick={selectPreviousGuideFilter}
          >
            <i className='fa-solid fa-arrow-left'></i>
          </button>

          <div className='modal__header-title'>
            <i className='fa-solid fa-wand-magic-sparkles'></i>
            <p>Smart Guide</p>
          </div>

          <button className='modal__header-close-btn modal__header-close-btn--hidden'>
            <i className='fa-solid fa-close'></i>
          </button>
        </header>
      )}

      {guideCompleted ? (
        <div className='wizard__completed'>
          <i className='fa-solid fa-stars'></i>
          <p>Your shortlist is ready!</p>
          <button
            onClick={() => {
              navigateTab('systems')
              navigateScreen('list')
              setWizardOpen(false)
            }}
          >
            View results
          </button>
        </div>
      ) : (
        <>
          {selectedFilter && (
            <>
              {selectedFilterSection && (
                <div className='wizard__section-title'>
                  <i className={`fa-solid ${selectedFilterSection.icon}`}></i>
                  <p>{selectedFilterSection.title}</p>
                </div>
              )}

              <FilterQuestion
                selectedFilter={selectedFilter}
                value={value}
                setValue={setValue}
                descExpanded={descExpanded}
                setDescExpanded={setDescExpanded}
                // To automatically move on when user selects
                // onChange={(value) => {
                //   // Set applied filter in store
                //   addOrUpdateAppliedFilter(selectedFilter, value)

                //   // Reset local state
                //   setDescExpanded(false)

                //   // Move on to next guide-filter
                //   selectNextGuideFilter()
                // }}

                // For save-button on each question
                onSave={
                  value !== undefined
                    ? () => {
                        // Set applied filter in store
                        addOrUpdateAppliedFilter(selectedFilter, value)

                        // Reset local state
                        setValue(undefined)
                        setDescExpanded(false)

                        // Mark guide as used at least once
                        setGuideUsed(true)

                        // Move on to next guide-filter
                        selectNextGuideFilter()
                      }
                    : undefined
                }
                // For skip-button on each question in wizard
                onSkip={() => {
                  // Reset local state
                  setValue(undefined)
                  setDescExpanded(false)

                  // Move on to next guide-filter
                  selectNextGuideFilter()
                }}
                inWizard
              />
            </>
          )}

          <WizardProgress />
        </>
      )}
    </div>
  )
}

const WizardProgress = () => {
  const guideCompleted = useFiltersStore((state) => state.guideCompleted)
  const visibleFilters = useVisibleFilters()
  const selectedVisibleIndex = useSelectedVisibleIndex()

  const total = visibleFilters.length
  const completed = guideCompleted ? total : Math.max(0, selectedVisibleIndex) // how many are done, NOT including current
  // const completed = guideCompleted ? total : Math.max(0, selectedVisibleIndex + 1) // how many are done, including current

  const left = Math.max(0, total - completed)
  const percent = total > 0 ? Math.round((completed / total) * 100) : 0

  return (
    <div className='wizard__progress'>
      <div className='wizard__progress-labels'>
        <p>Guide Progress</p>
        <p>
          {completed}/{total} answered
        </p>
      </div>

      <div
        className='wizard__progress-bar'
        role='progressbar'
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={percent}
        aria-label='Guide progress'
        aria-valuetext={`${completed} of ${total} completed, ${left} left`}
      >
        <div
          className='wizard__progress-bar-fill'
          style={{ width: `${percent}%` }}
        />
      </div>
    </div>
  )
}

export { Wizard }
